import moment from 'moment'
import React, { useEffect, useState } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiIcon, EuiLink, EuiPanel, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { MasterCampaignPlanCampaignBuilder, PostalCodeTarget } from 'api/entities/MasterCampaignStub'
import { getIntervalUnitLabel } from 'utils/MasterCampaignPlanUtils'

import BackgroundContentCard from '../BackgroundContentCard'

interface CampaignCardProps {
  builder: MasterCampaignPlanCampaignBuilder
}

export const CampaignCard = ({ builder }: CampaignCardProps) => {
  const [groupedPostalCodes, setGroupedPostalCodes] = useState<{ city: string; postalCodes: PostalCodeTarget[] }[]>([])
  const [isExpanded, setIsExpanded] = useState(true)

  useEffect(() => {
    const grouped: { city: string; postalCodes: PostalCodeTarget[] }[] = []

    builder.zips?.forEach(target => {
      if (!target.name?.includes(',')) {
        return
      }

      const [, city] = target.name.split(',')
      let postalCodeGroup = grouped.find(g => g.city === city)
      if (!grouped.find(g => g.city === city)) {
        postalCodeGroup = { city, postalCodes: [] }
        grouped.push(postalCodeGroup)
      }
      postalCodeGroup?.postalCodes.push(target)
    })

    setGroupedPostalCodes(grouped)
  }, [builder.zips])

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const campaignTypeDisplay = builder.isOffAdCritter && builder.offAdCritterName ? `${builder.offAdCritterName}` : builder.campaignType

  return (
    <>
      <div id={`campaign-${builder.id}`}>
        <EuiFlexGroup>
          <EuiFlexItem>
            <EuiPanel hasShadow={true} style={{ backgroundColor: '#ede7dd', padding: '3rem 5rem' }}>
            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiTitle size={'s'}>
                  <p>{builder.name}</p>
                </EuiTitle>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size='m' />
            <EuiFlexGroup>
              <EuiFlexItem>
                <span>
                  <b>Campaign Type:</b> {campaignTypeDisplay}
                  <br />
                  <b>Starts:</b> {moment(builder.startDate).format('MMMM Do YYYY')}
                  <br />
                  <b>Duration:</b> {builder.intervalDuration} {getIntervalUnitLabel(builder.intervalUnit, builder.intervalDuration ?? 1)} {builder.intervalFrequency && builder.intervalFrequency > 1 ? `(every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)})` : ''}
                </span>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiFlexGroup>
              <EuiFlexItem>
                <span>
                  <b>Budget:</b> {formatter.format(builder.intervalBudget)}
                  {builder.intervalFrequency && builder.intervalFrequency > 1 ? `/every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                  {builder.intervalFrequency && builder.intervalFrequency === 1 ? `/${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                </span>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size='l' />

            {builder.longSummary && (
              <EuiFlexGroup>
                <EuiFlexItem>
                  <b>Summary</b>
                  <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                  <EuiSpacer size='s' />
                  <EuiText size='s'>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{builder.longSummary}</p>
                  </EuiText>
                  <EuiSpacer size='l' />
                </EuiFlexItem>
              </EuiFlexGroup>
            )}

            {isExpanded && (
              <div>
                <EuiSpacer size='l' />

                {builder.seasonalitySummary && (
                  <EuiFlexGroup>
                    <EuiFlexItem>
                      <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                        <b>Timing</b>
                        <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                        <EuiText size='s'>
                          <p style={{ whiteSpace: 'pre-wrap' }}>{builder.seasonalitySummary}</p>
                        </EuiText>
                      </BackgroundContentCard>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                )}

                <EuiFlexGroup alignItems='stretch'>
                  {(builder.geoSummary || groupedPostalCodes.length > 0) && (
                    <EuiFlexItem grow={4}>
                      <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                        <>
                          <div>
                            <b>Geography</b>
                            <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                            {builder.geoSummary && (
                              <>
                                <EuiSpacer size='s' />
                                <EuiText size='s'>
                                  <p style={{ whiteSpace: 'pre-wrap' }}>{builder.geoSummary}</p>
                                </EuiText>
                              </>
                            )}
                          </div>
                          <EuiSpacer size={'m'} />
                          <ul style={{ marginLeft: '1rem' }}>
                            {groupedPostalCodes.map(({ city, postalCodes }, index) => (
                              <li key={index}>
                                <b>{city}</b>
                                <EuiSpacer size='xs' />
                                {postalCodes.map((postalCode, index) => (
                                  <ul key={index}>
                                    <li key={index}>{postalCode.code}</li>
                                  </ul>
                                ))}
                                <EuiSpacer size='s' />
                              </li>
                            ))}
                          </ul>
                        </>
                      </BackgroundContentCard>
                    </EuiFlexItem>
                  )}
                  {(builder.audienceSummary || (builder.segmentTargets?.length ?? 0) > 0) && (
                    <EuiFlexItem grow={6}>
                      <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                        <div>
                          <b>Audience Segments</b>
                          <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                          {builder.audienceSummary && (
                            <>
                              <EuiSpacer size='s' />
                              <EuiText size='s'>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{builder.audienceSummary}</p>
                              </EuiText>
                            </>
                          )}
                        </div>
                        <EuiSpacer size={'m'} />
                        <ul style={{ marginLeft: '1rem' }}>
                          {builder.segmentTargets.map((segment, index) => (
                            <li key={index}>
                              <b>{segment.hierarchy[segment.hierarchy.length - 1]}</b>
                              <div>
                                <EuiSpacer size='xs' />
                                {segment.name}
                                <EuiSpacer size='m' />
                              </div>
                            </li>
                          ))}
                        </ul>
                      </BackgroundContentCard>
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
              </div>
            )}
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    </>
  )
}
